import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './Register.css';

const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState(1); // Étape du formulaire (1: Entreprise, 2: Admin, 3: Plan)

  const [formData, setFormData] = useState({
    // Entreprise
    nomEntreprise: '',
    ninea: '',
    rc: '',
    telephoneEntreprise: '',
    adresseEntreprise: '',
    
    // Administrateur
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    password: '',
    confirmPassword: '',
    
    // Plan
    planId: '',
    acceptTerms: false
  });

  useEffect(() => {
    // Charger les plans disponibles
    const fetchPlans = async () => {
      try {
        const response = await axios.get('/api/abonnements/plans');
        setPlans(response.data);
        // Sélectionner le plan Starter par défaut
        if (response.data.length > 0) {
          setFormData(prev => ({ ...prev, planId: response.data[0]._id }));
        }
      } catch (error) {
        console.error('Erreur chargement plans:', error);
        // Plans par défaut si l'API échoue
        setPlans([
          { _id: 'starter', nom: 'Starter', prixMensuel: 100000, limites: { boutiques: 1, utilisateurs: 2 } },
          { _id: 'pro', nom: 'Pro', prixMensuel: 150000, limites: { boutiques: 3, utilisateurs: 5 } },
          { _id: 'entreprise', nom: 'Entreprise', prixMensuel: 300000, limites: { boutiques: 999999, utilisateurs: 999999 } }
        ]);
        setFormData(prev => ({ ...prev, planId: 'starter' }));
      }
    };
    fetchPlans();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const validateStep1 = () => {
    if (!formData.nomEntreprise.trim()) {
      setError('Le nom de l\'entreprise est requis');
      return false;
    }
    if (!formData.telephoneEntreprise.trim()) {
      setError('Le téléphone de l\'entreprise est requis');
      return false;
    }
    if (!formData.adresseEntreprise.trim()) {
      setError('L\'adresse de l\'entreprise est requise');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.nom.trim() || !formData.prenom.trim()) {
      setError('Nom et prénom sont requis');
      return false;
    }
    if (!formData.email.trim()) {
      setError('L\'email est requis');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Email invalide');
      return false;
    }
    if (!formData.telephone.trim()) {
      setError('Le téléphone est requis');
      return false;
    }
    if (!formData.password || formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.planId) {
      setError('Veuillez sélectionner un plan');
      return false;
    }
    if (!formData.acceptTerms) {
      setError('Vous devez accepter les conditions d\'utilisation');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
      setError('');
    } else if (step === 2 && validateStep2()) {
      setStep(3);
      setError('');
    }
  };

  const handleBack = () => {
    setStep(prev => Math.max(1, prev - 1));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep3()) return;

    setLoading(true);
    setError('');

    try {
      const registrationData = {
        entreprise: {
          nom: formData.nomEntreprise,
          ninea: formData.ninea || undefined,
          rc: formData.rc || undefined,
          telephone: formData.telephoneEntreprise,
          adresse: formData.adresseEntreprise
        },
        admin: {
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email,
          telephone: formData.telephone,
          password: formData.password
        },
        plan: formData.planId
      };

      const response = await axios.post('/api/auth/register', registrationData);
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Inscription réussie ! Vous bénéficiez de 30 jours d\'essai gratuit. Connectez-vous pour commencer.' 
          } 
        });
      }, 2000);

    } catch (error) {
      console.error('Erreur inscription:', error);
      setError(
        error.response?.data?.message || 
        'Une erreur est survenue lors de l\'inscription'
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = Array.isArray(plans) ? plans.find(p => p._id === formData.planId) : null;

  return (
    <div className="register-page">
      <div className="register-container">
        {/* Left Side - Info */}
        <div className="register-left">
          <div className="register-branding">
            <div className="brand-logo">
              <span className="brand-icon">📊</span>
              <span className="brand-name">gestStock</span>
            </div>
            <h1 className="register-heading">
              Commencez votre essai gratuit de <span className="highlight">30 jours</span>
            </h1>
            <p className="register-description">
              Aucune carte bancaire requise • Annulation à tout moment
            </p>

            <div className="benefits-list">
              <div className="benefit-item">
                <div className="benefit-icon">✨</div>
                <div className="benefit-text">
                  <h4>Configuration en 5 minutes</h4>
                  <p>Créez votre compte et commencez immédiatement</p>
                </div>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">🚀</div>
                <div className="benefit-text">
                  <h4>Toutes les fonctionnalités</h4>
                  <p>Accès complet pendant 30 jours</p>
                </div>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">🎯</div>
                <div className="benefit-text">
                  <h4>Support dédié</h4>
                  <p>Notre équipe vous accompagne</p>
                </div>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">🔒</div>
                <div className="benefit-text">
                  <h4>Données sécurisées</h4>
                  <p>Isolation multi-tenant garantie</p>
                </div>
              </div>
            </div>

            <div className="trust-badges">
              <div className="badge">
                <span className="badge-icon">🏆</span>
                <span>500+ Entreprises</span>
              </div>
              <div className="badge">
                <span className="badge-icon">⭐</span>
                <span>99.9% Uptime</span>
              </div>
              <div className="badge">
                <span className="badge-icon">🔐</span>
                <span>ISO 27001</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="register-right">
          <div className="register-form-container">
            {/* Progress Steps */}
            <div className="progress-steps">
              <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                <div className="step-number">1</div>
                <div className="step-label">Entreprise</div>
              </div>
              <div className={`progress-line ${step > 1 ? 'active' : ''}`}></div>
              <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
                <div className="step-number">2</div>
                <div className="step-label">Administrateur</div>
              </div>
              <div className={`progress-line ${step > 2 ? 'active' : ''}`}></div>
              <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
                <div className="step-number">3</div>
                <div className="step-label">Plan</div>
              </div>
            </div>

            {success ? (
              <div className="success-message">
                <div className="success-icon">✅</div>
                <h2>Inscription réussie !</h2>
                <p>Vous bénéficiez de 30 jours d'essai gratuit.</p>
                <p>Redirection vers la page de connexion...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="register-form">
                {/* Step 1: Entreprise */}
                {step === 1 && (
                  <div className="form-step">
                    <h2 className="form-title">Informations de l'entreprise</h2>
                    <p className="form-subtitle">Commencez par nous parler de votre entreprise</p>

                    <div className="form-group">
                      <label htmlFor="nomEntreprise">Nom de l'entreprise *</label>
                      <input
                        type="text"
                        id="nomEntreprise"
                        name="nomEntreprise"
                        value={formData.nomEntreprise}
                        onChange={handleChange}
                        placeholder="Ex: Mon Entreprise SARL"
                        required
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="ninea">NINEA (optionnel)</label>
                        <input
                          type="text"
                          id="ninea"
                          name="ninea"
                          value={formData.ninea}
                          onChange={handleChange}
                          placeholder="Ex: 0012345678"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="rc">Registre de Commerce (optionnel)</label>
                        <input
                          type="text"
                          id="rc"
                          name="rc"
                          value={formData.rc}
                          onChange={handleChange}
                          placeholder="Ex: SN-DKR-2024-A-12345"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="telephoneEntreprise">Téléphone de l'entreprise *</label>
                      <input
                        type="tel"
                        id="telephoneEntreprise"
                        name="telephoneEntreprise"
                        value={formData.telephoneEntreprise}
                        onChange={handleChange}
                        placeholder="+221 77 123 45 67"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="adresseEntreprise">Adresse de l'entreprise *</label>
                      <textarea
                        id="adresseEntreprise"
                        name="adresseEntreprise"
                        value={formData.adresseEntreprise}
                        onChange={handleChange}
                        placeholder="Adresse complète de votre entreprise"
                        rows="3"
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Step 2: Administrateur */}
                {step === 2 && (
                  <div className="form-step">
                    <h2 className="form-title">Compte administrateur</h2>
                    <p className="form-subtitle">Créez votre compte administrateur principal</p>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="nom">Nom *</label>
                        <input
                          type="text"
                          id="nom"
                          name="nom"
                          value={formData.nom}
                          onChange={handleChange}
                          placeholder="Votre nom"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="prenom">Prénom *</label>
                        <input
                          type="text"
                          id="prenom"
                          name="prenom"
                          value={formData.prenom}
                          onChange={handleChange}
                          placeholder="Votre prénom"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="votre.email@exemple.com"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="telephone">Téléphone *</label>
                      <input
                        type="tel"
                        id="telephone"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        placeholder="+221 77 123 45 67"
                        required
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="password">Mot de passe *</label>
                        <input
                          type="password"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Minimum 6 caractères"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="confirmPassword">Confirmer mot de passe *</label>
                        <input
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Répétez le mot de passe"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Plan */}
                {step === 3 && (
                  <div className="form-step">
                    <h2 className="form-title">Choisissez votre plan</h2>
                    <p className="form-subtitle">30 jours gratuits pour tous les plans</p>

                    <div className="plans-selection">
                      {Array.isArray(plans) && plans.map(plan => (
                        <div
                          key={plan._id}
                          className={`plan-card ${formData.planId === plan._id ? 'selected' : ''} ${plan.nom === 'Pro' ? 'recommended' : ''}`}
                          onClick={() => setFormData(prev => ({ ...prev, planId: plan._id }))}
                        >
                          {plan.nom === 'Pro' && <div className="recommended-badge">Recommandé</div>}
                          <h3>{plan.nom}</h3>
                          <div className="plan-price">
                            <span className="price">{plan.prixMensuel.toLocaleString('fr-FR')}</span>
                            <span className="currency">XOF/mois</span>
                          </div>
                          <div className="plan-trial">
                            <span className="trial-badge">🎁 30 jours gratuits</span>
                          </div>
                          <ul className="plan-features">
                            <li>
                              ✓ {plan.limites.boutiques === 999999 ? 'Boutiques illimitées' : `${plan.limites.boutiques} boutique(s)`}
                            </li>
                            <li>
                              ✓ {plan.limites.utilisateurs === 999999 ? 'Utilisateurs illimités' : `${plan.limites.utilisateurs} utilisateur(s)`}
                            </li>
                            <li>✓ Gestion complète des stocks</li>
                            <li>✓ Point de vente (POS)</li>
                            <li>✓ Génération de factures</li>
                            {plan.nom === 'Pro' && <li>✓ Statistiques avancées</li>}
                            {plan.nom === 'Entreprise' && <li>✓ Support prioritaire 24/7</li>}
                          </ul>
                          <div className="plan-select">
                            <input
                              type="radio"
                              id={`plan-${plan._id}`}
                              name="planId"
                              value={plan._id}
                              checked={formData.planId === plan._id}
                              onChange={handleChange}
                            />
                            <label htmlFor={`plan-${plan._id}`}>
                              {formData.planId === plan._id ? 'Sélectionné' : 'Sélectionner'}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="acceptTerms"
                          checked={formData.acceptTerms}
                          onChange={handleChange}
                          required
                        />
                        <span>
                          J'accepte les <Link to="/terms">conditions d'utilisation</Link> et la{' '}
                          <Link to="/privacy">politique de confidentialité</Link>
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="error-message">
                    <span className="error-icon">⚠️</span>
                    {error}
                  </div>
                )}

                {/* Form Actions */}
                <div className="form-actions">
                  {step > 1 && (
                    <button
                      type="button"
                      onClick={handleBack}
                      className="btn-back"
                      disabled={loading}
                    >
                      ← Retour
                    </button>
                  )}
                  {step < 3 ? (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="btn-next"
                    >
                      Continuer →
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="btn-submit"
                      disabled={loading}
                    >
                      {loading ? 'Inscription...' : 'Créer mon compte'}
                    </button>
                  )}
                </div>

                {/* Login Link */}
                <div className="form-footer">
                  <p>
                    Vous avez déjà un compte ?{' '}
                    <Link to="/login" className="login-link">Se connecter</Link>
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
